import type { Dict } from "./types";

export const en: Dict = {
  app: {
    name: "Aksaraku",
    tagline: "Learn while playing",
  },
  common: {
    on: "On",
    off: "Off",
    back: "Back",
    soon: "Coming soon",
    loading: "Just a moment...",
  },
  nav: {
    parent: "Parents",
    home: "Home",
    writing: "Writing",
    games: "Games",
    collection: "Collection",
    avatar: "Character",
    groupLearn: "Learn",
    groupParent: "Parents",
    mainLabel: "Main navigation",
    offlineReady: "Ready offline",
    offlineActive: "Offline mode",
    mainProfile: "Main profile",
  },
  profiles: {
    title: "Who is playing?",
    subtitle: "Pick a child or add a new profile",
    add: "Add Child",
    namePlaceholder: "Child name",
    language: "Language",
    start: "Start",
    empty: "No profiles yet. Add a child first.",
    errors: {
      emptyName: "Name is required",
      nameTooLong: "Name is too long",
      invalidLocale: "Unknown language",
    },
  },
  home: {
    greeting: "Hi, {{name}}!",
    tracing: "Writing",
    games: "Games",
    rewards: "Collection",
    avatar: "Character",
    subtitle: "What shall we learn today?",
    streak: "{{count}} days",
    continue: {
      eyebrow: "Keep learning",
      cta: "Continue",
      detail: "{{category}} · {{current}} of {{total}} mastered",
    },
    stats: {
      stars: "{{count}} stars",
      progress: "{{current}} of {{total}} letters mastered",
    },
    hint: {
      tracing: "Letters, numbers & shapes",
      games: "3 fun mini-games",
      rewards: "Stickers, trophies & characters",
      avatar: "Customise your friend",
    },
    progress: {
      title: "Letter progress",
      sub: "{{current}} of {{total}} letters mastered",
      letters: "Letters {{current}}/{{total}}",
      numbers: "Numbers {{current}}/{{total}}",
      shapes: "Shapes {{current}}/{{total}}",
    },
    reward: {
      title: "Next reward",
      count: "{{current}}/{{target}}",
    },
  },
  play: {
    title: "Let's Write",
    pick: "Pick a letter, number, or shape",
    retry: "Try Again",
    next: "Next",
    great: "Great job!",
    strokeProgress: "Line {{current}} of {{total}}",
    offTrack: "Follow the line",
    listen: "Listen",
    canvasLabel: "Writing area for {{glyph}}",
    objectHint: "{{glyph}} for {{object}}",
    color: "Color",
    letterTitle: "Letter {{glyph}}",
    digitTitle: "Number {{glyph}}",
    shapeTitle: "Shapes",
    digitSub: "Writing · 1 to 10",
    shapeSub: "Writing · {{current}} of {{total}}",
    ratio: "{{current}} / {{total}}",
    pickShape: "Pick another shape",
    tipLetter: "Start at the blue dot and follow the orange dots slowly.",
    tipDigit: "Follow the arrow from the first dot. Take your time!",
    tipShape: "Follow the line slowly, try not to wander off.",
    dragHint: "Drag from the blue dot along the orange dots",
    progressTitle: "Progress",
    reset: "Try again",
    finish: "Done",
    nextDigit: "Next number {{glyph}}",
    nextGlyph: "Next letter {{glyph}}",
    nextShape: "Next shape {{name}}",
    retryGlyph: "Redo letter {{glyph}}",
    retryShape: "Redo shape {{name}}",
    colorGlyph: "Color letter {{glyph}}",
    doneTitle: "Great job, {{name}}!",
    doneSub: "You wrote {{glyph}}. Keep going!",
    chipStars: "{{count}} stars",
    chipLines: "{{count}} lines done",
    masteryLabel: "{{current}} of {{total}} mastered",
    linesCount: "{{count}} lines",
    pickerHint: "Tap a letter to start writing",
    unitUpper: "{{count}} letters",
    unitLower: "{{count}} lowercase letters",
    unitDigit: "{{count}} numbers",
    unitShape: "{{count}} shapes",
    recommendation: "Recommended for {{name}}",
    shape: {
      circle: "Circle",
      oval: "Ellipse",
      square: "Square",
      rectangle: "Rectangle",
      triangle: "Triangle",
      star: "Star",
      heart: "Heart",
      line: "Line",
    },
  },
  games: {
    title: "Games",
    matching: "Matching",
    balloon: "Pop the Balloon",
    coloring: "Coloring",
    pick: "Pick your favourite game",
    matchingSub: "Match uppercase and lowercase letters",
    balloonSub: "Pop balloons with letters",
    coloringSub: "Colour numbers and shapes",
    play: "Play",
    footer: "Every finished game earns a star for your collection!",
    progressTitle: "Play progress",
    starsEarned: "{{count}} stars",
    played: "{{count}} games played",
    progressSub: "Each finished game earns one star.",
    nextReward: "Next reward",
    nextRewardSub: "Collect 3 stars to unlock a new sticker.",
    modeCase: "Big - Small",
    modeObject: "Letter - Picture",
    casePrompt: "Find the lowercase letter",
    objectPrompt: "Which letter is this?",
    balloonPrompt: "Pop the balloon: {{letter}}",
    repeat: "Repeat",
    correct: "Correct!",
    wrong: "Try again",
    playAgain: "Play Again",
    score: "{{correct}} of {{total}} correct",
    colorHint: "Pick a color, then tap the letter",
    stickers: "Stickers",
    clear: "Clear",
    finish: "Finish",
    finished: "Great work!",
    paint: "Color",
    painted: "{{painted}} of {{total}} parts colored",
  },
  preview: {
    title: "Content Preview",
    hint: "Check the shape of every letter, number, and shape.",
  },
  rewards: {
    title: "Reward Collection",
    pick: "Stickers, trophies and characters",
    summary: "{{unlocked}} of {{total}} rewards unlocked",
    footer: "Finish activities to unlock new rewards.",
    kind: {
      sticker: "Stickers",
      trophy: "Trophies",
      character: "Characters",
    },
    unlocked: "You earned a new reward!",
    yeay: "Yay!",
    locked: "Locked",
    done: "Done",
    requirement: {
      itemsAttempted: "Try {{count}} activities",
      itemsMastered: "Master {{count}} letters or numbers",
      starsEarned: "Earn {{count}} stars",
      totalAttempts: "Play {{count}} times",
      kindMastered: "Master {{count}} in one category",
    },
  },
  avatar: {
    title: "My Character",
    pick: "Customise your friend",
    footer: "Unlock new parts by collecting rewards.",
    slot: {
      color: "Color",
      ears: "Ears",
      eyes: "Eyes",
      accessory: "Accessory",
    },
  },
  categories: {
    upper: "Uppercase",
    lower: "Lowercase",
    digits: "Numbers",
    shapes: "Shapes",
  },
  mastery: {
    new: "New",
    learning: "Learning",
    familiar: "Getting there",
    mastered: "Mastered",
  },
  parent: {
    title: "Parent Area",
    subtitle: "Track progress and manage the app",
    gateTitle: "Parents Only",
    gate: {
      question: "What is {{a}} + {{b}}?",
      placeholder: "Answer",
      submit: "Enter",
      wrong: "That answer is not right",
      cancel: "Cancel",
    },
    progressFor: "{{name}}'s progress",
    progressQuick: "{{mastered}} mastered • {{attempted}} tried • ⭐ {{stars}}",
    report: "Progress Report",
    navReport: "Report",
    summary: "Summary",
    statMastered: "Mastered",
    statAttempted: "Tried",
    statStars: "Stars",
    statAttempts: "Total Attempts",
    levels: "Mastery Levels",
    byCategory: "By Category",
    needsPractice: "Needs Practice",
    noWeak: "Everything tried so far is mastered. Well done!",
    practiceDetail: "Accuracy {{score}}% • {{attempts}} tries",
    tracingQuality: "Writing Quality",
    noTracing: "No writing practice yet.",
    avgAccuracy: "Average accuracy",
    traces: "Practice count",
    neverPlayed: "No activity yet.",
    lastPlayed: "Last played: {{date}}",
    screenTime: "Time Limit",
    sessionLimit: "Session limit",
    breakReminder: "Break reminder",
    breakOff: "Off",
    minutes: "{{minutes}} minutes",
    sessionRemaining: "Time left in this session: {{time}}",
    settings: "Settings",
    language: "Language",
    haptics: "Vibration",
    audio: "Sound",
    reduceMotion: "Reduce motion",
    switchProfile: "Switch Child",
    preview: "Content Preview",
    manageChildren: "Manage Children",
    activeChild: "Active",
    switchTo: "Select",
    delete: "Delete",
    deleteConfirm: "Delete for sure?",
    deleteHint: "Deleting a child also removes their progress and collection.",
    lastChildHint: "There must always be at least one child.",
    childLanguage: "Language for {{name}}",
    cancel: "Cancel",
  },
  session: {
    loading: "One moment…",
    expiredTitle: "Time for a break",
    expiredHint:
      "Today's learning session is over. Ask a parent to unlock it again.",
    parentUnlock: "Parent",
    breakReminder: "You have been learning for {{minutes}} minutes. Let's rest!",
    breakDone: "Rested",
  },
  settings: {
    errors: {
      invalidLocale: "Invalid language",
      invalidSessionLimit: "Invalid session limit",
      invalidBreakReminder: "Invalid break reminder",
    },
  },
  error: {
    storage: "Could not save data. Please try again.",
    retry: "Try Again",
    reset: "Reset Data",
    resetHint: "Deletes all profiles and progress on this device.",
    crash: "Oops, something went wrong. Let's try that again.",
    crashRetry: "Try Again",
  },
  landing: {
    nav: {
      skip: "Skip to content",
    },
    hero: {
      tagline: "Learn letters while playing",
      subtitle: "Trace letters, numbers and shapes for ages 3+. It keeps working without internet.",
      mascotGreeting: "Hello!",
    },
    languages: {
      id: "Indonesia",
      en: "English",
      ar: "العربية",
    },
    onboarding: {
      age: "Ages 3+",
      offline: "Works offline",
      languages: "3 languages",
      safe: "Kid-safe",
      languageLabel: "CHOOSE LANGUAGE · BAHASA",
      cta: "Start learning",
      parentLink: "For parents · manage profiles & screen time",
    },
    install: {
      instructions: "How to Install",
      ios: "Tap the Share button, then choose \"Add to Home Screen\".",
      android: "Tap the menu, then choose \"Add to Home screen\".",
      desktop: "Click the install icon in the address bar, or use the browser menu.",
      openApp: "Open App",
    },
  },
};
